/**
 * @quotech/ui - Utility Functions
 *
 * Shared general-purpose utilities used across ALL hubs.
 * Formatting-specific helpers live in ./format.ts.
 */

import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

// ---------------------------------------------------------------------------
// Tailwind helpers
// ---------------------------------------------------------------------------

/**
 * Merge Tailwind CSS classes with proper precedence.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ---------------------------------------------------------------------------
// Keyboard / platform helpers
// ---------------------------------------------------------------------------

/**
 * Format a keyboard shortcut for display.
 */
export function formatShortcut(key: string, meta = true): string {
  const mac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const metaKey = mac ? '⌘' : 'Ctrl'
  return meta ? `${metaKey}+${key.toUpperCase()}` : key.toUpperCase()
}

/**
 * Check if we're on a Mac.
 */
export function isMac(): boolean {
  if (typeof navigator === 'undefined') return false
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0
}

/** Platform-aware label for the universal search shortcut (⌘F / Ctrl+F). */
export function formatModFShortcut(): string {
  return isMac() ? '⌘F' : 'Ctrl+F'
}

/**
 * True when the app is running INSIDE the Quotech desktop (Electron) shell.
 *
 * The desktop preload exposes `window.quotechDesktop` (see
 * desktop/preload.js); we also accept the Electron user-agent as a fallback.
 * Returns false in a normal browser tab, so "Download the desktop app"
 * affordances only show to web visitors who don't already have the app.
 */
export function isDesktopApp(): boolean {
  if (typeof window !== 'undefined' && (window as { quotechDesktop?: unknown }).quotechDesktop) {
    return true
  }
  if (typeof navigator !== 'undefined' && /electron/i.test(navigator.userAgent || '')) {
    return true
  }
  return false
}

// ---------------------------------------------------------------------------
// String helpers
// ---------------------------------------------------------------------------

/**
 * Truncate text with ellipsis.
 *
 * @example truncate('Hello World', 7) // "Hell..."
 */
export function truncate(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

/**
 * Truncate a filename while preserving the extension.
 *
 * @example truncateFilename('annual-report-2025.pdf', 20) // "annual-repor....pdf"
 */
export function truncateFilename(filename: string, maxLength = 30): string {
  if (filename.length <= maxLength) return filename
  const ext = filename.split('.').pop() || ''
  const name = filename.slice(0, filename.lastIndexOf('.'))
  const truncatedName = name.slice(0, maxLength - ext.length - 4) + '...'
  return `${truncatedName}.${ext}`
}

/**
 * Generate initials from a full name.
 *
 * @example getInitials('John Smith')  // "JS"
 * @example getInitials('Alice')       // "A"
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Capitalise the first letter of a string.
 */
export function capitalize(text: string): string {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * Convert snake_case or kebab-case to Title Case.
 *
 * @example toTitleCase('pending_review') // "Pending Review"
 */
export function toTitleCase(text: string): string {
  return text
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

// ---------------------------------------------------------------------------
// Function helpers
// ---------------------------------------------------------------------------

/**
 * Debounce a function call.
 */
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Sleep for a given number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// ---------------------------------------------------------------------------
// Value checks
// ---------------------------------------------------------------------------

/**
 * Check if a value is empty (null, undefined, empty string, empty array, empty object).
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

